import React, { useState, useEffect, useMemo, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, X, Layers, Clock, Copy, Link, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { API } from "./SharedComponents";
import DeloadConfirmModal from "./DeloadConfirmModal";
import BlockEditor from "./BlockEditor";
import { ExerciseForm } from "./ExerciseLibrary";

const LINK_TYPES = [
  { value: "superset", label: "Superset", desc: "Back-to-back, minimal rest", color: "amber" },
  { value: "circuit", label: "Circuit", desc: "Rotate through all exercises", color: "emerald" },
  { value: "straight", label: "Straight Sets", desc: "Grouped, but do all sets of each", color: "blue" },
];

const WorkoutForm = ({ workout, onClose, onSave }) => {
  // Parse existing workout into unified blocks (each block = one exercise slot with levels)
  const parseWorkoutToBlocks = (existingWorkout) => {
    if (!existingWorkout) return [];
    const blocks = [];
    const progressionSlots = existingWorkout.progression_slots || [];

    if (existingWorkout.sections) {
      existingWorkout.sections.forEach((section, si) => {
        const isCircuitSection = section.section_type === "circuit" || section.section_type === "superset";
        const hasMultipleExercises = section.exercises?.length > 1;
        const groupId = (isCircuitSection && hasMultipleExercises) ? `cg_${si}_${Date.now()}` : null;
        const linkType = section.link_type || (isCircuitSection ? "superset" : null);
        const blockGroup = section.name?.replace("Block ", "").replace("Superset ", "").replace("Circuit ", "").replace("Straight ", "").charAt(0) || String.fromCharCode(65 + si);

        section.exercises?.forEach((ex, ei) => {
          const blockName = `${blockGroup}${ei + 1}`;

          const setsData = ex.sets_data && ex.sets_data.length > 0
            ? ex.sets_data
            : Array(ex.sets || 1).fill(null).map((_, i) => ({
                set_num: i + 1, percent_max: "", reps: ex.reps || "", rest_mins: ex.rest_seconds ? (ex.rest_seconds / 60).toString() : "3"
              }));

          blocks.push({
            id: `block_${blocks.length}`, name: blockName, block_group: blockGroup, isCircuit: isCircuitSection,
            circuit_group: groupId,
            link_type: linkType,
            coach_note: ei === 0 ? (section.coach_note || "") : "",
            levels: [{ level: 1, name: "", exercise_id: ex.exercise_id || "", exercise_name: ex.exercise_name || "", rm_type: "", bw_multiplier: "", advancement_type: "bw_multiplier", advancement_value: "", based_off_exercise_id: ex.based_off_exercise_id || "", based_off_exercise_name: ex.based_off_exercise_name || "", alt_exercise_id: ex.alt_exercise_id || "", alt_exercise_name: ex.alt_exercise_name || "", alt_bar_equipment_id: ex.alt_bar_equipment_id || null, alt_sets_data: ex.alt_sets_data || null, sets_data: setsData }]
          });
        });
      });
    }

    progressionSlots.forEach(ps => {
      let matched = false;
      for (const block of blocks) {
        const l1Ex = block.levels[0]?.exercise_id;
        const psL1Ex = ps.levels?.[0]?.exercise_id;
        if ((block.name === ps.name) || (l1Ex && psL1Ex && l1Ex === psL1Ex)) {
          block.name = ps.name;
          for (let i = 0; i < ps.levels.length; i++) {
            const psLevel = ps.levels[i];
            if (i === 0) {
              block.levels[0].rm_type = psLevel.rm_type || "";
              block.levels[0].bw_multiplier = psLevel.bw_multiplier || "";
              block.levels[0].advancement_type = psLevel.advancement_type || (psLevel.bw_multiplier ? "bw_multiplier" : "bw_multiplier");
              block.levels[0].advancement_value = psLevel.advancement_value || psLevel.bw_multiplier || "";
              if (psLevel.based_off_exercise_id) { block.levels[0].based_off_exercise_id = psLevel.based_off_exercise_id; block.levels[0].based_off_exercise_name = psLevel.based_off_exercise_name; }
              if (psLevel.alt_exercise_id) { block.levels[0].alt_exercise_id = psLevel.alt_exercise_id; block.levels[0].alt_exercise_name = psLevel.alt_exercise_name; block.levels[0].alt_bar_equipment_id = psLevel.alt_bar_equipment_id; block.levels[0].alt_sets_data = psLevel.alt_sets_data; }
              if (!block.levels[0].exercise_id && psLevel.exercise_id) {
                block.levels[0].exercise_id = psLevel.exercise_id;
                block.levels[0].exercise_name = psLevel.exercise_name;
              }
            } else {
              block.levels.push({
                level: i + 1, name: psLevel.name || "", exercise_id: psLevel.exercise_id || "", exercise_name: psLevel.exercise_name || "",
                rm_type: psLevel.rm_type || "", bw_multiplier: psLevel.bw_multiplier || "",
                advancement_type: psLevel.advancement_type || (psLevel.bw_multiplier ? "bw_multiplier" : "bw_multiplier"),
                advancement_value: psLevel.advancement_value || psLevel.bw_multiplier || "",
                based_off_exercise_id: psLevel.based_off_exercise_id || "", based_off_exercise_name: psLevel.based_off_exercise_name || "",
                alt_exercise_id: psLevel.alt_exercise_id || "", alt_exercise_name: psLevel.alt_exercise_name || "", alt_bar_equipment_id: psLevel.alt_bar_equipment_id || null, alt_sets_data: psLevel.alt_sets_data || null,
                sets_data: (psLevel.sets || []).map((s, si) => ({ set_num: si + 1, percent_max: s.percent?.toString() || "", reps: s.reps?.toString() || "", rest_mins: s.rest_mins?.toString() || "3" }))
              });
            }
          }
          matched = true;
          break;
        }
      }
      if (!matched) {
        blocks.push({
          id: `block_${blocks.length}`, name: ps.name, block_group: ps.name.charAt(0) || String.fromCharCode(65 + blocks.length), isCircuit: false,
          levels: ps.levels.map((lv, i) => ({
            level: i + 1, name: lv.name || "", exercise_id: lv.exercise_id || "", exercise_name: lv.exercise_name || "",
            rm_type: lv.rm_type || "", bw_multiplier: lv.bw_multiplier || "",
            advancement_type: lv.advancement_type || (lv.bw_multiplier ? "bw_multiplier" : "bw_multiplier"),
            advancement_value: lv.advancement_value || lv.bw_multiplier || "",
            based_off_exercise_id: lv.based_off_exercise_id || "", based_off_exercise_name: lv.based_off_exercise_name || "",
            alt_exercise_id: lv.alt_exercise_id || "", alt_exercise_name: lv.alt_exercise_name || "", alt_bar_equipment_id: lv.alt_bar_equipment_id || null, alt_sets_data: lv.alt_sets_data || null,
            sets_data: (lv.sets || []).map((s, si) => ({ set_num: si + 1, percent_max: s.percent?.toString() || "", reps: s.reps?.toString() || "", rest_mins: s.rest_mins?.toString() || "3" }))
          }))
        });
      }
    });

    return blocks;
  };

  const initialBlocks = parseWorkoutToBlocks(workout);
  const [formData, setFormData] = useState({
    title: workout?.title || "",
    times_per_week: workout?.times_per_week || null,
    blocks: initialBlocks.length > 0 ? initialBlocks
      : [{ id: "block_0", name: "A1", block_group: "A", isCircuit: false, circuit_group: null, link_type: null, coach_note: "", levels: [{ level: 1, name: "", exercise_id: "", exercise_name: "", rm_type: "", bw_multiplier: "", advancement_type: "bw_multiplier", advancement_value: "", sets_data: [{ set_num: 1, percent_max: "100", reps: "", rest_mins: "3" }] }] }],
    tier_required: workout?.tier_required || "foundations",
    category: workout?.category || "strength",
    estimated_duration: workout?.estimated_duration || 45,
    subtabs: workout?.subtabs || [],
  });

  const [exercises, setExercises] = useState([]);
  const [exercisesLoading, setExercisesLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [showExerciseSearch, setShowExerciseSearch] = useState(null);
  const [searchMode, setSearchMode] = useState(null); // null = main exercise, "based_off", "alt"
  const [replacingExercise, setReplacingExercise] = useState(null);
  const [showDeloadConfirm, setShowDeloadConfirm] = useState(false);
  const [activeLevelTab, setActiveLevelTab] = useState({});
  const [exerciseFormState, setExerciseFormState] = useState(null); // { mode: 'edit', exercise } or { mode: 'create', blockIndex, levelIndex }

  // Subtabs available in this category (loaded from API)
  const [availableSubtabs, setAvailableSubtabs] = useState([]);
  const [showNewSubtabInput, setShowNewSubtabInput] = useState(false);
  const [newSubtabName, setNewSubtabName] = useState("");

  const fetchSubtabs = useCallback(async () => {
    try {
      const r = await fetch(
        `${API}/coach/subtabs?library_type=workouts&category=${formData.category}`,
        { credentials: "include" }
      );
      if (r.ok) {
        const d = await r.json();
        setAvailableSubtabs(d.subtabs || []);
      }
    } catch (e) {
      console.error("subtabs fetch failed", e);
    }
  }, [formData.category]);

  useEffect(() => { fetchSubtabs(); }, [fetchSubtabs]);

  const toggleSubtab = (name) => {
    setFormData((f) => {
      const cur = f.subtabs || [];
      return cur.includes(name)
        ? { ...f, subtabs: cur.filter((s) => s !== name) }
        : { ...f, subtabs: [...cur, name] };
    });
  };

  const createSubtab = async () => {
    const name = newSubtabName.trim();
    if (!name) return;
    try {
      const r = await fetch(`${API}/coach/subtabs`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, library_type: "workouts", category: formData.category }),
      });
      if (r.ok) {
        await fetchSubtabs();
        toggleSubtab(name); // auto-assign the new one to this workout
        setNewSubtabName("");
        setShowNewSubtabInput(false);
      } else if (r.status === 409) {
        // Already exists — just toggle it
        toggleSubtab(name);
        setNewSubtabName("");
        setShowNewSubtabInput(false);
      }
    } catch (e) {
      console.error("subtab create failed", e);
    }
  };

  const fetchExercises = useCallback(async (retry = 0) => {
    try {
      setExercisesLoading(true);
      const res = await fetch(`${API}/coach/exercises?limit=1000`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setExercises(data.exercises || []);
      } else if (retry < 2) {
        console.warn(`[WorkoutForm] Exercise fetch returned ${res.status}, retrying...`);
        setTimeout(() => fetchExercises(retry + 1), 1000);
      } else {
        console.error(`[WorkoutForm] Exercise fetch failed after retries: ${res.status}`);
      }
    } catch (err) {
      console.error("[WorkoutForm] Exercise fetch error:", err);
      if (retry < 2) {
        setTimeout(() => fetchExercises(retry + 1), 1000);
      }
    } finally {
      setExercisesLoading(false);
    }
  }, []);

  useEffect(() => { fetchExercises(); }, [fetchExercises]);

  const applyDeload = () => {
    const newBlocks = formData.blocks.map(block => ({
      ...block,
      levels: block.levels.map(level => ({
        ...level,
        sets_data: level.sets_data.map(set => ({ ...set, reps: set.reps ? Math.ceil(parseInt(set.reps) / 2).toString() : set.reps }))
      }))
    }));
    setFormData({ ...formData, blocks: newBlocks });
    setShowDeloadConfirm(false);
    toast.success("Deload applied! All reps halved.");
  };

  const filteredExercises = useMemo(() => {
    if (!searchQuery.trim()) return [];
    const terms = searchQuery.toLowerCase().split(/\s+/).filter(t => t.length > 0);
    return exercises.filter(ex => { const n = ex.name.toLowerCase(); return terms.every(t => n.includes(t)); }).slice(0, 50);
  }, [exercises, searchQuery]);

  // Block helpers — blocks are containers, exercises are items within them
  const getBlockGroups = () => {
    const order = [];
    const map = {};
    formData.blocks.forEach((b, i) => {
      const g = b.block_group || "A";
      if (!map[g]) { map[g] = []; order.push(g); }
      map[g].push({ block: b, index: i });
    });
    return { order, map };
  };

  const addBlock = () => {
    const { order } = getBlockGroups();
    let next = "A";
    while (order.includes(next) && next.charCodeAt(0) < 91) next = String.fromCharCode(next.charCodeAt(0) + 1);
    setFormData(prev => ({
      ...prev,
      blocks: [...prev.blocks, { id: `block_${Date.now()}`, name: `${next}1`, block_group: next, isCircuit: false, circuit_group: null, link_type: null, coach_note: "", levels: [{ level: 1, name: "", exercise_id: "", exercise_name: "", rm_type: "", bw_multiplier: "", advancement_type: "bw_multiplier", advancement_value: "", sets_data: [{ set_num: 1, percent_max: "100", reps: "", rest_mins: "3" }] }] }]
    }));
  };

  const addExerciseToBlock = (groupName) => {
    const groupExercises = formData.blocks.filter(b => b.block_group === groupName);
    const newName = `${groupName}${groupExercises.length + 1}`;
    const lastInGroup = formData.blocks.reduce((last, b, i) => b.block_group === groupName ? i : last, -1);
    const newBlock = {
      id: `block_${Date.now()}`, name: newName, block_group: groupName,
      isCircuit: false, circuit_group: null, link_type: null, coach_note: "",
      levels: [{ level: 1, name: "", exercise_id: "", exercise_name: "", rm_type: "", bw_multiplier: "", advancement_type: "bw_multiplier", advancement_value: "", sets_data: [{ set_num: 1, percent_max: "100", reps: "", rest_mins: "3" }] }]
    };
    const newBlocks = [...formData.blocks];
    newBlocks.splice(lastInGroup + 1, 0, newBlock);
    setFormData(prev => ({ ...prev, blocks: newBlocks }));
  };

  const removeExercise = (bi) => {
    if (formData.blocks.length === 1) { toast.error("Must have at least one exercise"); return; }
    setFormData(prev => ({ ...prev, blocks: prev.blocks.filter((_, i) => i !== bi) }));
  };

  const removeBlock = removeExercise; // backwards compat alias

  const moveExerciseInBlock = (bi, dir) => {
    const block = formData.blocks[bi];
    const group = block.block_group;
    const groupIndices = formData.blocks.map((b, i) => b.block_group === group ? i : -1).filter(i => i >= 0);
    const posInGroup = groupIndices.indexOf(bi);
    const targetPos = posInGroup + dir;
    if (targetPos < 0 || targetPos >= groupIndices.length) return;
    const targetFlatIdx = groupIndices[targetPos];
    const newBlocks = [...formData.blocks];
    [newBlocks[bi], newBlocks[targetFlatIdx]] = [newBlocks[targetFlatIdx], newBlocks[bi]];
    setFormData(prev => ({ ...prev, blocks: newBlocks }));
  };

  const moveBlock = moveExerciseInBlock; // backwards compat alias

  const moveExerciseToBlock = (bi, targetGroupName) => {
    const newBlocks = [...formData.blocks];
    const block = { ...newBlocks[bi], block_group: targetGroupName };
    newBlocks.splice(bi, 1);
    const lastInTarget = newBlocks.reduce((last, b, i) => b.block_group === targetGroupName ? i : last, -1);
    newBlocks.splice(lastInTarget + 1, 0, block);
    setFormData(prev => ({ ...prev, blocks: newBlocks }));
    toast.success(`Moved to Block ${targetGroupName}`);
  };

  const moveGroup = (groupName, dir) => {
    const { order } = getBlockGroups();
    const gi = order.indexOf(groupName);
    const targetGi = gi + dir;
    if (targetGi < 0 || targetGi >= order.length) return;
    const targetGroup = order[targetGi];

    // Rebuild the flat array with groups swapped
    const newOrder = [...order];
    [newOrder[gi], newOrder[targetGi]] = [newOrder[targetGi], newOrder[gi]];
    const newBlocks = [];
    newOrder.forEach(g => {
      formData.blocks.forEach(b => { if (b.block_group === g) newBlocks.push(b); });
    });
    setFormData(prev => ({ ...prev, blocks: newBlocks }));
  };

  const toggleCircuit = (bi) => {
    const b = [...formData.blocks]; b[bi] = { ...b[bi], isCircuit: !b[bi].isCircuit }; setFormData({ ...formData, blocks: b });
  };
  const updateBlockName = (bi, name) => {
    const b = [...formData.blocks]; b[bi] = { ...b[bi], name }; setFormData({ ...formData, blocks: b });
  };

  // Set link type for all exercises in a block_group
  const setGroupLinkType = (groupName, linkType) => {
    const groupId = linkType ? `cg_${groupName}_${Date.now()}` : null;
    const newBlocks = formData.blocks.map(b =>
      b.block_group === groupName
        ? { ...b, link_type: linkType, circuit_group: groupId, isCircuit: linkType && linkType !== "straight" }
        : b
    );
    setFormData(prev => ({ ...prev, blocks: newBlocks }));
    if (linkType) {
      const label = LINK_TYPES.find(t => t.value === linkType)?.label || linkType;
      toast.success(`Block ${groupName} set to ${label}`);
    }
  };

  // Level helpers
  const addLevelToBlock = (bi) => {
    const b = [...formData.blocks]; const levels = [...b[bi].levels];
    levels.push({ level: levels.length + 1, name: "", exercise_id: "", exercise_name: "", rm_type: "", bw_multiplier: "", advancement_type: "bw_multiplier", advancement_value: "", sets_data: [{ set_num: 1, percent_max: "100", reps: "", rest_mins: "3" }] });
    b[bi] = { ...b[bi], levels }; setFormData({ ...formData, blocks: b });
    setActiveLevelTab({ ...activeLevelTab, [bi]: levels.length - 1 });
  };
  const removeLevelFromBlock = (bi, li) => {
    const b = [...formData.blocks]; const levels = b[bi].levels.filter((_, i) => i !== li);
    levels.forEach((lv, i) => { lv.level = i + 1; }); b[bi] = { ...b[bi], levels };
    setFormData({ ...formData, blocks: b });
    if ((activeLevelTab[bi] || 0) >= levels.length) setActiveLevelTab({ ...activeLevelTab, [bi]: Math.max(0, levels.length - 1) });
  };
  const moveLevelInBlock = (bi, li, dir) => {
    const ni = li + dir;
    const b = [...formData.blocks]; const levels = [...b[bi].levels];
    if (ni < 0 || ni >= levels.length) return;
    [levels[li], levels[ni]] = [levels[ni], levels[li]];
    levels.forEach((lv, i) => { lv.level = i + 1; });
    b[bi] = { ...b[bi], levels };
    setFormData({ ...formData, blocks: b });
    setActiveLevelTab({ ...activeLevelTab, [bi]: ni });
  };
  const updateLevelField = (bi, li, field, value) => {
    setFormData(prev => {
      const b = [...prev.blocks]; const levels = [...b[bi].levels];
      levels[li] = { ...levels[li], [field]: value }; b[bi] = { ...b[bi], levels };
      return { ...prev, blocks: b };
    });
  };

  const updateMultipleLevelFields = (bi, li, updates) => {
    setFormData(prev => {
      const b = [...prev.blocks]; const levels = [...b[bi].levels];
      levels[li] = { ...levels[li], ...updates }; b[bi] = { ...b[bi], levels };
      return { ...prev, blocks: b };
    });
  };

  const clearAltExercise = (bi, li) => {
    setFormData(prev => {
      const b = [...prev.blocks]; const levels = [...b[bi].levels];
      levels[li] = { ...levels[li], alt_exercise_id: "", alt_exercise_name: "", alt_sets_data: null };
      b[bi] = { ...b[bi], levels };
      return { ...prev, blocks: b };
    });
  };

  const clearBasedOff = (bi, li) => {
    setFormData(prev => {
      const b = [...prev.blocks]; const levels = [...b[bi].levels];
      levels[li] = { ...levels[li], based_off_exercise_id: "", based_off_exercise_name: "" };
      b[bi] = { ...b[bi], levels };
      return { ...prev, blocks: b };
    });
  };

  // Exercise selection
  const getRepsFromRM = (ex) => {
    if (ex?.rm_value) { const m = ex.rm_value.match(/(\d+)/); if (m) return m[1]; }
    if (ex?.name) { const m = ex.name.match(/(\d+)RM/i); if (m) return m[1]; }
    return "";
  };
  const selectExerciseForLevel = (bi, li, exercise) => {
    const b = [...formData.blocks]; const levels = [...b[bi].levels];
    if (searchMode === "based_off") {
      levels[li] = { ...levels[li], based_off_exercise_id: exercise.exercise_id, based_off_exercise_name: exercise.name };
    } else if (searchMode === "alt") {
      levels[li] = { ...levels[li], alt_exercise_id: exercise.exercise_id, alt_exercise_name: exercise.name, alt_bar_equipment_id: exercise.bar_equipment_id || null };
    } else {
      const reps = getRepsFromRM(exercise);
      levels[li] = { ...levels[li], exercise_id: exercise.exercise_id, exercise_name: exercise.name, bar_equipment_id: exercise.bar_equipment_id || null, rm_type: exercise.rm_value || levels[li].rm_type, sets_data: levels[li].sets_data.map(s => ({ ...s, reps: reps || s.reps })) };
    }
    b[bi] = { ...b[bi], levels }; setFormData({ ...formData, blocks: b });
    setShowExerciseSearch(null); setReplacingExercise(null); setSearchQuery(""); setSearchMode(null);
  };
  const clearExerciseFromLevel = (bi, li) => {
    const b = [...formData.blocks]; const levels = [...b[bi].levels];
    levels[li] = { ...levels[li], exercise_id: "", exercise_name: "" };
    b[bi] = { ...b[bi], levels }; setFormData({ ...formData, blocks: b });
  };

  // Sets helpers
  const updateSetsCount = (bi, li, delta) => {
    const b = [...formData.blocks]; const levels = [...b[bi].levels]; const level = { ...levels[li] };
    const sd = [...level.sets_data]; const nc = Math.max(1, Math.min(20, sd.length + delta));
    while (sd.length < nc) { const prev = sd[sd.length - 1]; sd.push({ set_num: sd.length + 1, percent_max: prev?.percent_max || "100", reps: prev?.reps || "", rest_mins: prev?.rest_mins || "3" }); }
    if (nc < sd.length) sd.length = nc;
    level.sets_data = sd; levels[li] = level; b[bi] = { ...b[bi], levels }; setFormData({ ...formData, blocks: b });
  };
  const updateSetData = (bi, li, si, field, value) => {
    const b = [...formData.blocks]; const levels = [...b[bi].levels]; const level = { ...levels[li] };
    const sd = [...level.sets_data]; sd[si] = { ...sd[si], [field]: value };
    level.sets_data = sd; levels[li] = level; b[bi] = { ...b[bi], levels }; setFormData({ ...formData, blocks: b });
  };

  // Convert to API format — group exercises by block_group into sections
  const convertBlocksToSections = () => {
    const { order, map } = getBlockGroups();
    const sections = [];

    order.forEach(groupName => {
      const items = map[groupName];
      const linkType = items[0]?.block?.link_type;
      const exercises = items.map(({ block }, idx) => {
        const l1 = block.levels[0];
        if (!l1?.exercise_id) return null;
        return {
          exercise_id: l1.exercise_id, exercise_name: l1.exercise_name,
          bar_equipment_id: l1.bar_equipment_id || null,
          based_off_exercise_id: l1.based_off_exercise_id || null,
          based_off_exercise_name: l1.based_off_exercise_name || null,
          alt_exercise_id: l1.alt_exercise_id || null,
          alt_exercise_name: l1.alt_exercise_name || null,
          alt_bar_equipment_id: l1.alt_bar_equipment_id || null,
          alt_sets_data: l1.alt_sets_data || null,
          sets: l1.sets_data?.length || 1,
          reps: l1.sets_data?.[0]?.reps || "",
          rest_seconds: Math.round(parseFloat(l1.sets_data?.[0]?.rest_mins || "3") * 60),
          sets_data: l1.sets_data || [], order: idx
        };
      }).filter(Boolean);

      if (exercises.length === 0) return;

      const sectionType = (linkType && linkType !== "straight" && exercises.length > 1) ? "circuit" : "main";
      const typeLabel = linkType === "circuit" ? "Circuit" : linkType === "straight" ? "Straight" : linkType === "superset" ? "Superset" : "Block";
      sections.push({
        section_type: sectionType,
        link_type: linkType || null,
        name: `${typeLabel} ${groupName}`,
        coach_note: items[0]?.block?.coach_note || "",
        exercises, rounds: 1, rest_between_rounds: 60
      });
    });
    return sections;
  };
  const extractProgressionSlots = () => {
    return formData.blocks.filter(b => b.levels.length > 1 || b.levels.some(lv => lv.bw_multiplier || lv.advancement_value)).map(b => ({
      name: b.name,
      levels: b.levels.map((lv, i) => ({ level: i + 1, name: lv.name || "", exercise_id: lv.exercise_id, exercise_name: lv.exercise_name, bar_equipment_id: lv.bar_equipment_id || null, rm_type: lv.rm_type, bw_multiplier: lv.bw_multiplier || "", advancement_type: lv.advancement_type || "bw_multiplier", advancement_value: lv.advancement_value || lv.bw_multiplier || "", based_off_exercise_id: lv.based_off_exercise_id || null, based_off_exercise_name: lv.based_off_exercise_name || null, alt_exercise_id: lv.alt_exercise_id || null, alt_exercise_name: lv.alt_exercise_name || null, alt_bar_equipment_id: lv.alt_bar_equipment_id || null, alt_sets_data: lv.alt_sets_data || null, sets: lv.sets_data.map(s => ({ percent: s.percent_max, reps: s.reps, rest_mins: s.rest_mins })) }))
    }));
  };

  // After saving an exercise edit, update the name in all blocks that reference it
  const handleExerciseFormSaved = (exerciseData) => {
    if (!exerciseData) {
      // Create mode from library — no specific exercise to link (batch created)
      setExerciseFormState(null);
      fetchExercises(); // refresh exercise list
      return;
    }
    if (exerciseFormState?.mode === 'edit') {
      // Edit mode — update exercise name in all blocks that reference it
      const exerciseId = exerciseData.exercise_id;
      const newName = exerciseData.name;
      setFormData(prev => ({
        ...prev,
        blocks: prev.blocks.map(b => ({
          ...b,
          levels: b.levels.map(lv =>
            lv.exercise_id === exerciseId ? { ...lv, exercise_name: newName } : lv
          )
        }))
      }));
    } else if (exerciseFormState?.mode === 'create') {
      // Create & Replace — select the new exercise into the block
      const { blockIndex, levelIndex } = exerciseFormState;
      selectExerciseForLevel(blockIndex, levelIndex, {
        exercise_id: exerciseData.exercise_id,
        name: exerciseData.name,
        rm_value: exerciseData.rm_value,
        bar_equipment_id: exerciseData.bar_equipment_id,
      });
      fetchExercises(); // refresh exercise list
    }
    setExerciseFormState(null);
  };

  // Open full exercise form for editing
  const openExerciseEdit = async (exerciseId) => {
    try {
      const res = await fetch(`${API}/coach/exercises/${exerciseId}`, { credentials: "include" });
      if (res.ok) {
        const ex = await res.json();
        setExerciseFormState({ mode: 'edit', exercise: ex });
      } else {
        toast.error("Failed to load exercise");
      }
    } catch {
      toast.error("Failed to load exercise");
    }
  };

  // Open full exercise form for Create & Replace
  const openExerciseCreate = (blockIndex, levelIndex) => {
    setExerciseFormState({ mode: 'create', blockIndex, levelIndex });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title.trim()) { toast.error("Please enter a workout name"); return; }
    setSaving(true);
    try {
      const payload = { title: formData.title, description: workout?.description || "", category: formData.category, difficulty: "intermediate", times_per_week: formData.times_per_week, estimated_duration: formData.estimated_duration, tier_required: formData.tier_required, sections: convertBlocksToSections(), tags: workout?.tags || [], subtabs: formData.subtabs || [], progression_slots: extractProgressionSlots() };
      const url = workout ? `${API}/coach/workout-templates/${workout.workout_id}` : `${API}/coach/workout-templates`;
      const res = await fetch(url, { method: workout ? "PUT" : "POST", headers: { "Content-Type": "application/json" }, credentials: "include", body: JSON.stringify(payload) });
      if (res.ok) { toast.success(workout ? "Workout updated!" : "Workout created!"); onSave(); }
      else { const err = await res.json().catch(() => ({})); console.error("Workout save failed:", res.status, err); toast.error(err.detail || `Failed to save workout (${res.status})`); }
    } catch (err) { toast.error("Something went wrong"); }
    finally { setSaving(false); }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }} className="bg-[#0F1419] border border-[#1E293B] rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="workout-form-modal">
        <div className="sticky top-0 bg-[#0F1419] border-b border-[#1E293B] px-6 py-4 flex items-center justify-between z-10">
          <h2 className="font-heading text-xl font-bold">{workout ? "Edit Workout" : "Create Workout"}</h2>
          <button type="button" onClick={onClose} className="p-2 hover:bg-[#1E293B] rounded-lg"><X className="w-5 h-5" /></button>
        </div>

        <DeloadConfirmModal show={showDeloadConfirm} onCancel={() => setShowDeloadConfirm(false)} onConfirm={applyDeload} />

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Workout Name & Level */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-gray-400 mb-2">Workout Name *</label>
              <input type="text" value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} placeholder="e.g., Upper Body Strength" className="w-full bg-[#0B0F1A] border border-[#1E293B] rounded-lg px-4 py-3 text-white focus:border-primary outline-none" required data-testid="workout-name-input" />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-2">Times Per Week</label>
              <div className="flex gap-2">
                {[2, 3, 4, 5, 6].map((tpw) => (
                  <button key={tpw} type="button" onClick={() => setFormData({ ...formData, times_per_week: formData.times_per_week === tpw ? null : tpw })} className={`flex-1 py-3 rounded-lg font-medium transition-colors ${formData.times_per_week === tpw ? "bg-primary text-white" : "bg-[#1E293B] text-gray-400 hover:bg-[#2D3B4F]"}`} data-testid={`tpw-${tpw}`}>{tpw}x/wk</button>
                ))}
              </div>
            </div>
          </div>

          {/* Category, Duration, Tier */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm text-gray-400 mb-2">Category</label>
              <select value={formData.category} onChange={(e) => setFormData({ ...formData, category: e.target.value })} className="w-full bg-[#0B0F1A] border border-[#1E293B] rounded-lg px-4 py-3 text-white">
                {["strength", "speed", "stamina", "suppleness", "skill", "extras"].map(c => <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-2">Duration (mins)</label>
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-gray-500" />
                <input type="number" value={formData.estimated_duration} onChange={(e) => setFormData({ ...formData, estimated_duration: parseInt(e.target.value) || 0 })} className="w-full bg-[#0B0F1A] border border-[#1E293B] rounded-lg px-4 py-3 text-white" data-testid="duration-input" />
              </div>
            </div>
          </div>

          {/* Assign To */}
          <div>
            <label className="block text-sm text-gray-400 mb-2">Assign To</label>
            <select value={formData.tier_required || "just_for_me"} onChange={(e) => setFormData({ ...formData, tier_required: e.target.value })}
              className="w-full bg-[#0B0F1A] border border-[#1E293B] rounded-lg px-4 py-3 text-white focus:border-primary outline-none" data-testid="workout-tier-select">
              <option value="just_for_me">Sachin's Personal Workouts</option>
              <option value="foundations">Foundations</option>
              <option value="accelerator">Advanced Accelerator</option>
              <option value="elite">Elite</option>
            </select>
          </div>

          {/* Subtabs */}
          <div data-testid="workout-subtabs-picker">
            <label className="block text-sm text-gray-400 mb-2">
              Subtabs <span className="text-xs text-gray-500">(optional — groups this workout under a custom tab in the library)</span>
            </label>
            <div className="flex flex-wrap gap-2">
              {availableSubtabs.length === 0 && !showNewSubtabInput && (
                <p className="text-xs text-gray-500 italic mr-2 self-center">
                  No subtabs yet for this category.
                </p>
              )}
              {availableSubtabs.map((s) => {
                const picked = (formData.subtabs || []).includes(s.name);
                return (
                  <button
                    key={s.subtab_id || s.name}
                    type="button"
                    onClick={() => toggleSubtab(s.name)}
                    className={`px-3 py-1.5 rounded-full text-sm font-semibold transition-colors border ${
                      picked
                        ? "bg-amber-500/20 border-amber-500/60 text-amber-300"
                        : "bg-[#0B0F1A] border-[#1E293B] text-gray-300 hover:border-amber-500/40"
                    }`}
                    data-testid={`subtab-chip-${(s.name || "").replace(/\s+/g, "-").toLowerCase().slice(0, 40)}`}
                  >
                    {picked ? "✓ " : ""}{s.name}
                  </button>
                );
              })}

              {showNewSubtabInput ? (
                <div className="flex items-center gap-1">
                  <input
                    autoFocus
                    value={newSubtabName}
                    onChange={(e) => setNewSubtabName(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && createSubtab()}
                    placeholder="New subtab name…"
                    className="bg-[#0B0F1A] border border-[#1E293B] rounded-full px-3 py-1.5 text-sm text-white focus:border-amber-500/60 outline-none"
                    data-testid="new-subtab-input"
                    maxLength={40}
                  />
                  <button
                    type="button"
                    onClick={createSubtab}
                    className="px-3 py-1.5 bg-amber-500 hover:bg-amber-400 text-black font-bold rounded-full text-sm"
                    data-testid="new-subtab-create"
                  >
                    Add
                  </button>
                  <button
                    type="button"
                    onClick={() => { setShowNewSubtabInput(false); setNewSubtabName(""); }}
                    className="text-gray-500 hover:text-white text-xs px-2"
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setShowNewSubtabInput(true)}
                  className="px-3 py-1.5 bg-[#0B0F1A] border border-dashed border-[#1E293B] hover:border-amber-500/40 rounded-full text-sm text-gray-400 hover:text-amber-400 transition-colors"
                  data-testid="add-new-subtab-btn"
                >
                  + New subtab
                </button>
              )}
            </div>
          </div>

          {/* Blocks */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-lg flex items-center gap-2"><Layers className="w-5 h-5 text-primary" /> Blocks</h3>
              <button type="button" onClick={() => setShowDeloadConfirm(true)} className="flex items-center gap-2 bg-yellow-600/20 text-yellow-400 px-3 py-1.5 rounded-lg text-sm font-medium hover:bg-yellow-600/30" data-testid="deload-btn">
                <Copy className="w-4 h-4" /> Make Deload
              </button>
            </div>

            <div className="space-y-4">
              {(() => {
                const { order, map } = getBlockGroups();
                return order.map((groupName, gi) => {
                  const items = map[groupName];
                  const linkType = items[0]?.block?.link_type;
                  const hasLink = !!linkType;
                  const borderColor = linkType === "circuit" ? "border-emerald-500/40" : linkType === "straight" ? "border-blue-500/40" : linkType === "superset" ? "border-amber-500/40" : "border-[#1E293B]";
                  const linkBgColor = linkType === "circuit" ? "bg-emerald-500/5" : linkType === "straight" ? "bg-blue-500/5" : linkType === "superset" ? "bg-amber-500/5" : "";
                  const linkTextColor = linkType === "circuit" ? "text-emerald-400" : linkType === "straight" ? "text-blue-400" : linkType === "superset" ? "text-amber-400" : "text-primary";

                  return (
                    <div key={groupName} className={`rounded-xl border ${borderColor} ${linkBgColor} overflow-hidden`} data-testid={`block-group-${groupName}`}>
                      {/* Block header */}
                      <div className="flex items-center justify-between px-4 py-2.5 bg-[#141B2D] border-b border-[#1E293B]">
                        <div className="flex items-center gap-3">
                          <span className="text-sm font-bold text-white">Block {groupName}</span>
                          <span className="text-[11px] text-gray-500">{items.length} exercise{items.length !== 1 ? "s" : ""}</span>
                          {items.length > 1 && (
                            <select
                              value={linkType || ""}
                              onChange={(e) => setGroupLinkType(groupName, e.target.value || null)}
                              className={`bg-[#0B0F1A] border border-[#2D3B4F] rounded-md px-2 py-0.5 text-[11px] font-medium ${linkTextColor} cursor-pointer outline-none`}
                              data-testid={`link-type-${groupName}`}
                            >
                              <option value="">No Link</option>
                              {LINK_TYPES.map(lt => <option key={lt.value} value={lt.value}>{lt.label}</option>)}
                            </select>
                          )}
                        </div>
                        <div className="flex items-center gap-1">
                          <button type="button" onClick={() => moveGroup(groupName, -1)} disabled={gi === 0} className="p-1 text-gray-500 hover:text-white disabled:opacity-20 transition-colors" title="Move block up">
                            <ChevronDown className="w-4 h-4 rotate-180" />
                          </button>
                          <button type="button" onClick={() => moveGroup(groupName, 1)} disabled={gi === order.length - 1} className="p-1 text-gray-500 hover:text-white disabled:opacity-20 transition-colors" title="Move block down">
                            <ChevronDown className="w-4 h-4" />
                          </button>
                        </div>
                      </div>

                      {/* Exercises in this block */}
                      <div className="p-2 space-y-2">
                        {items.map(({ block, index: blockIndex }, exIdx) => (
                          <BlockEditor
                            key={block.id}
                            block={block}
                            blockIndex={blockIndex}
                            totalBlocks={formData.blocks.length}
                            activeLevelIndex={activeLevelTab[blockIndex] || 0}
                            onSetActiveLevelTab={(bi, li) => setActiveLevelTab({ ...activeLevelTab, [bi]: li })}
                            onMoveBlock={moveExerciseInBlock}
                            onRemoveBlock={removeExercise}
                            onToggleCircuit={toggleCircuit}
                            onUpdateBlockName={updateBlockName}
                            onAddLevel={addLevelToBlock}
                            onRemoveLevel={removeLevelFromBlock}
                            onMoveLevel={moveLevelInBlock}
                            onUpdateLevelField={updateLevelField}
                            onUpdateMultipleLevelFields={updateMultipleLevelFields}
                            onUpdateSetsCount={updateSetsCount}
                            onUpdateSetData={updateSetData}
                            showExerciseSearch={showExerciseSearch}
                            replacingExercise={replacingExercise}
                            searchQuery={searchQuery}
                            filteredExercises={filteredExercises}
                            onOpenSearch={(bi, li, mode) => { setSearchMode(mode || null); setReplacingExercise(null); setShowExerciseSearch({ blockIndex: bi, levelIndex: li }); setSearchQuery(""); if (exercises.length === 0) fetchExercises(); }}
                            onOpenReplace={(bi, li) => { setSearchMode(null); setReplacingExercise({ blockIndex: bi, levelIndex: li }); setShowExerciseSearch({ blockIndex: bi, levelIndex: li }); setSearchQuery(""); if (exercises.length === 0) fetchExercises(); }}
                            onCloseSearch={() => { setReplacingExercise(null); setShowExerciseSearch(null); setSearchQuery(""); setSearchMode(null); }}
                            onSearchChange={setSearchQuery}
                            onSelectExercise={(ex) => selectExerciseForLevel(showExerciseSearch?.blockIndex, showExerciseSearch?.levelIndex, ex)}
                            onClearExercise={clearExerciseFromLevel}
                            isInSuperset={hasLink}
                            onAddExercise={() => addExerciseToBlock(groupName)}
                            onCreateAndReplace={openExerciseCreate}
                            onClearAltExercise={clearAltExercise}
                            onClearBasedOff={clearBasedOff}
                            exercisesLoading={exercisesLoading}
                            totalExercises={exercises.length}
                            onRetryLoad={fetchExercises}
                            exercisePosition={exIdx}
                            totalExercisesInBlock={items.length}
                            allBlockGroups={order}
                            currentBlockGroup={groupName}
                            onMoveToBlock={moveExerciseToBlock}
                            onEditExercise={(exId) => openExerciseEdit(exId)}
                            onCreateAndReplace={(bi, li) => openExerciseCreate(bi, li)}
                            onUpdateCoachNote={(bi, note) => {
                              setFormData(prev => ({
                                ...prev,
                                blocks: prev.blocks.map((b, i) => i === bi ? { ...b, coach_note: note } : b)
                              }));
                            }}
                          />
                        ))}
                      </div>

                      {/* Add exercise to this block */}
                      <button
                        type="button"
                        onClick={() => addExerciseToBlock(groupName)}
                        className="w-full py-2 text-xs text-gray-500 hover:text-primary hover:bg-[#141B2D] transition-colors flex items-center justify-center gap-1.5 border-t border-[#1E293B]"
                        data-testid={`add-exercise-to-${groupName}`}
                      >
                        <Plus className="w-3.5 h-3.5" /> Add Exercise
                      </button>
                    </div>
                  );
                });
              })()}
            </div>

            <button type="button" onClick={addBlock} className="w-full mt-4 py-3 border-2 border-dashed border-[#1E293B] rounded-xl text-gray-400 hover:border-primary hover:text-primary transition-colors flex items-center justify-center gap-2" data-testid="add-block-btn">
              <Plus className="w-5 h-5" /> Add Block
            </button>
          </div>

          {/* Submit */}
          <div className="flex gap-4 pt-6 border-t border-[#1E293B]">
            <button type="button" onClick={onClose} className="flex-1 bg-[#1E293B] text-white py-3 rounded-lg font-semibold hover:bg-[#2D3B4F] transition-colors">Cancel</button>
            <button type="submit" disabled={saving} className="flex-1 bg-primary text-white py-3 rounded-lg font-semibold hover:bg-blue-600 transition-colors disabled:opacity-50" data-testid="save-workout-btn">{saving ? "Saving..." : workout ? "Update Workout" : "Create Workout"}</button>
          </div>
        </form>
      </motion.div>

      {/* Full Exercise Form Modal */}
      <AnimatePresence>
        {exerciseFormState && (
          <ExerciseForm
            exercise={exerciseFormState.mode === 'edit' ? exerciseFormState.exercise : null}
            muscleGroups={[]}
            equipment={[]}
            onClose={() => setExerciseFormState(null)}
            onSave={handleExerciseFormSaved}
            onDuplicate={(newEx, existingEx) => {
              // For duplicates in create mode, use the existing exercise
              if (exerciseFormState.mode === 'create') {
                handleExerciseFormSaved({
                  exercise_id: existingEx.exercise_id,
                  name: existingEx.name,
                  rm_value: existingEx.rm_value,
                  bar_equipment_id: existingEx.bar_equipment_id,
                });
              }
            }}
          />
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default WorkoutForm;
